import React, { useState } from "react";
import { Formik } from "formik";
import Grid from "@mui/material/Grid";
import PasswordStrengthBar from "react-password-strength-bar";
import { Button } from "react-bootstrap";
import { GenericModal, MaterialInputField, PhoneInputField } from "components";
import { signUpSchema } from "common/schemas";
import { ENUMS, BASE_CURRENCY, MEMBERSHIP_PRICE } from "common";
import {
  performSignUpThroughFirebase,
  checkIfEmailExists,
  saveUserData,
} from "services/firebaseServices";
import { setTempUserDetail, getTempUserDetail } from "utilities/localStorage";
import { formatDate } from "pages/home";
import { CircularProgress } from "@mui/material";

const AddUser = ({ setShowModal, companyName, fetchBusinessInfo }) => {
  const [apiResponse, setApiResponse] = useState({
    type: ENUMS.MODAL_TYPES.ERROR,
    title: "",
    body: "",
  });
  const [open, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const [userDetails, setUserDetails] = useState({
    avatar: "",
    firstName: "",
    lastName: "",
    email: "",
    birthDate: "",
    gender: "",
    cprNumber: "",
    nationality: "",
    phone: "",
    ssn: "",
    donarNumber: "",
    address: "",
    insuranceType: "Choose",
    insuranceCompany: "",
    policyNumber: "",
    emergencyNumber: "+1",
    emergencyName: "",
    emergencyPhone: "",
    emergencyRelation: "",
    password: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    organDonation: "",
    cardOrderStatus: "",
    cardOrderDate: "",
    isActiveSubscription: true,
    isForcedRefreshToken: false,
    memberSince: formatDate(new Date()),
    cardReOrderStatus: "",
    cardReOrderDate: "",
    homephone: "",
    confirmPassword: "",
    company: companyName,
    relationShip: "",
    price: MEMBERSHIP_PRICE,
    currency: BASE_CURRENCY.toLowerCase(),
  });

  // --- Firebase and signup functions ---
  const performSignUp = async (payload) => {
    try {
      const response = await performSignUpThroughFirebase(payload);
      if (response) {
        const {
          user: { uid },
        } = response;
        const item = { ...userDetails };
        setTempUserDetail({ uid, item });
        saveDataToFirebase(uid);
      }
    } catch (error) {
      setShowModal(true);
      setApiResponse({
        type: ENUMS.MODAL_TYPES.ERROR,
        title: "Error",
        body: "Email already exists.",
      });
      console.error("SignUp Error:", error);
    }
  };

  const saveDataToFirebase = async (id) => {
    let getData = await getTempUserDetail();
    if (getData && getData.item) {
      const response = await saveUserData(id, getData.item);
      if (response === undefined) {
        setOpen(true);
        localStorage.setItem("welcome", "true");
        setApiResponse({
          type: ENUMS.MODAL_TYPES.SUCCESS,
          title: "Success",
          body: "Your Account Has been Created",
        });
      }
    } else {
      setOpen(true);
      setApiResponse({
        type: ENUMS.MODAL_TYPES.ERROR,
        title: "Error",
        body: "Unable to save user data. Please try again.",
      });
    }
  };

  const checkEmail = async (payload, props) => {
    setLoading(true);
    const response = await checkIfEmailExists(payload?.email);
    if (response) {
      setUserDetails((data) => ({
        ...data,
        email: payload.email,
        password: payload.password,
        confirmPassword: payload.confirmPassword,
      }));
      performSignUp(payload);
    } else {
      setApiResponse({
        type: ENUMS.MODAL_TYPES.ERROR,
        title: "Error",
        body: "Email already exists.",
      });
      setOpen(true);
    }
    setLoading(false);
  };

  // --- Step 1 Form ---
  function Step1(props) {
    return (
      <Formik
        initialValues={{
          email: userDetails.email,
          password: userDetails.password,
          confirmPassword: "",
          phone: userDetails.phone,
        }}
        validateOnMount
        validationSchema={signUpSchema}
        onSubmit={(values) => {
          setUserDetails((data) => ({
            ...data,
            phone: values.phone,
          }));
          checkEmail(values, props);
        }}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          touched,
          errors,
          values,
        }) => (
          <form onSubmit={handleSubmit} className="sign-up-step-cont">
            <div className="sign-up-form-heading mb-3">
              <span>Create User Account</span>
            </div>
            <Grid container spacing={0.7}>
              <Grid item xs={12}>
                <MaterialInputField
                  name="email"
                  id="email"
                  placeholder="Email"
                  label="Email"
                  error={touched.email && errors.email}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  value={values.email}
                />
              </Grid>
              <Grid item xs={12}>
                <MaterialInputField
                  id="password"
                  name="password"
                  placeholder="Password"
                  label="Password"
                  error={touched.password && errors.password}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  value={values.password}
                  isPassword
                />
                {values.password && (
                  <PasswordStrengthBar password={values.password} />
                )}
              </Grid>
              <Grid item xs={12}>
                <MaterialInputField
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  label="Confirm Password"
                  error={touched.confirmPassword && errors.confirmPassword}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  value={values.confirmPassword}
                  isPassword
                />
                {values.confirmPassword && (
                  <PasswordStrengthBar password={values.confirmPassword} />
                )}
              </Grid>
              <Grid item xs={12}>
                <PhoneInputField
                  name="phone"
                  id="phone"
                  value={values.phone}
                  handleChange={handleChange("phone")}
                  handleBlur={handleBlur("phone")}
                  specialLabel="Phone Number"
                  error={touched.phone && errors.phone}
                />
              </Grid>
            </Grid>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button
                variant="danger"
                type="button"
                onClick={() => setShowModal(false)}
              >
                Close
              </Button>
              <Button disabled={isLoading} variant="primary" type="submit">
                {!isLoading ? "Submit" : <CircularProgress size={15} />}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    );
  }

  return (
    <>
      <GenericModal
        show={open}
        type={apiResponse.type}
        title={apiResponse.title}
        body={apiResponse.body}
        buttonText={"ok"}
        handleClose={() => {
          setOpen(false);
          if (apiResponse.type == ENUMS.MODAL_TYPES.SUCCESS) {
            setOpen(false);
            setShowModal(false);
            fetchBusinessInfo();
          }
        }}
      />
      <Step1 />
    </>
  );
};

export default AddUser;
